import axios from 'axios';

// Se REACT_APP_BACKEND_URL e impostato lo usiamo (ambiente di anteprima Emergent).
// Se e vuoto, usiamo URL RELATIVI same-origin: l'app funziona su qualsiasi host
// (IP temporaneo, *.nip.io, dominio finale) con un'unica build, senza ricostruire.
const ENV_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

export const API = ENV_URL ? `${ENV_URL}/api` : '/api';

// -----------------------------------------------------------------------------
// Gestione token (localStorage) — auth deployment-agnostica + MULTI-SESSIONE.
// I cookie httpOnly restano un livello aggiuntivo, ma il token Bearer da
// localStorage e' la fonte primaria (il backend preferisce il Bearer al cookie).
//
// SESSIONI MULTI-RUOLO NELLO STESSO BROWSER:
//   I token sono partizionati per "audience" (customer vs staff). Ogni SCHEDA
//   ricorda la propria audience in sessionStorage, cosi' si puo' restare loggati
//   contemporaneamente come Cliente (Area Clienti) in una scheda e come Hotel /
//   Admin / Staff in un'altra, senza che un login sovrascriva l'altro.
//   - staff/admin/stakeholder -> chiavi LEGACY (retrocompatibili)
//   - customer                -> chiavi dedicate
// -----------------------------------------------------------------------------
const ACCESS_KEY = 'sila_access_token';         // staff/admin/stakeholder (legacy)
const REFRESH_KEY = 'sila_refresh_token';
const CUSTOMER_ACCESS_KEY = 'sila_customer_access_token';
const CUSTOMER_REFRESH_KEY = 'sila_customer_refresh_token';
const AUDIENCE_KEY = 'sila_audience';

function _keysFor(aud) {
  return aud === 'customer'
    ? { access: CUSTOMER_ACCESS_KEY, refresh: CUSTOMER_REFRESH_KEY }
    : { access: ACCESS_KEY, refresh: REFRESH_KEY };
}

function _detectAudience() {
  try {
    const saved = sessionStorage.getItem(AUDIENCE_KEY);
    if (saved === 'customer' || saved === 'staff') return saved;
  } catch { /* noop */ }
  const path = (typeof window !== 'undefined' && window.location && window.location.pathname) || '';
  const aud = path.startsWith('/cliente') ? 'customer' : 'staff';
  try { sessionStorage.setItem(AUDIENCE_KEY, aud); } catch { /* noop */ }
  return aud;
}

let _audience = _detectAudience();

export const tokenStore = {
  getAudience: () => _audience,
  setAudience: (aud) => {
    _audience = aud === 'customer' ? 'customer' : 'staff';
    try { sessionStorage.setItem(AUDIENCE_KEY, _audience); } catch { /* noop */ }
  },
  getAccess: () => { try { return localStorage.getItem(_keysFor(_audience).access); } catch { return null; } },
  getRefresh: () => { try { return localStorage.getItem(_keysFor(_audience).refresh); } catch { return null; } },
  set: (access, refresh) => {
    const k = _keysFor(_audience);
    try {
      if (access) localStorage.setItem(k.access, access);
      if (refresh) localStorage.setItem(k.refresh, refresh);
    } catch { /* storage non disponibile */ }
  },
  clear: () => {
    const k = _keysFor(_audience);
    try { localStorage.removeItem(k.access); localStorage.removeItem(k.refresh); } catch { /* noop */ }
  },
};

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor: allega sempre il Bearer se presente in localStorage.
api.interceptors.request.use((config) => {
  const t = tokenStore.getAccess();
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// Response interceptor: su 401 tenta UN refresh e riprova la richiesta originale.
let refreshing = null;
api.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = original.url || '';
    const isAuthCall = url.includes('/auth/login') || url.includes('/auth/refresh') || url.includes('/auth/register');
    const hasToken = !!(tokenStore.getAccess() || tokenStore.getRefresh());
    if (status === 401 && !original._retry && !isAuthCall && hasToken) {
      original._retry = true;
      try {
        if (!refreshing) {
          const rt = tokenStore.getRefresh();
          refreshing = api
            .post('/auth/refresh', rt ? { refresh_token: rt } : {})
            .then(({ data }) => {
              tokenStore.set(data.access_token, data.refresh_token);
              return data.access_token;
            })
            .finally(() => { refreshing = null; });
        }
        const newToken = await refreshing;
        if (newToken) {
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${newToken}`;
          return api(original);
        }
      } catch (e) {
        tokenStore.clear();
      }
    }
    return Promise.reject(error);
  }
);

// WebSocket URL (wss/ws). Usa ENV_URL se presente, altrimenti l'host corrente.
export function getWsUrl(token) {
  let base;
  if (ENV_URL) {
    const url = new URL(ENV_URL);
    const proto = url.protocol === 'https:' ? 'wss:' : 'ws:';
    base = `${proto}//${url.host}/api/ws`;
  } else {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    base = `${proto}//${window.location.host}/api/ws`;
  }
  return token ? `${base}?token=${encodeURIComponent(token)}` : base;
}

export function formatApiError(detail) {  if (detail == null) return 'Si e verificato un errore. Riprova.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

// Build a browser-usable URL for a media file returned by the backend.
// - Absolute http(s) URLs are returned as-is.
// - Relative "/api/..." paths are prefixed with ENV_URL when set (preview),
//   otherwise kept relative (same-origin production).
export function mediaUrl(path) {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return `${ENV_URL}${path}`;
}
